/**
 * dagTooltips — tooltip content builders for DAG nodes and edges.
 *
 * Pure functions that turn provenance data into compact tooltip strings.
 * Used by node components (CommentBadgeNode, PlotNode, SweepGroupNode)
 * and the edge hover overlay in DAGView.
 */

import type { DAGNode } from "../../api/artifacts";
import type { SweepGroup } from "./dagGrouping";

const KIND_LABELS: Record<string, string> = {
  geometry: "Geometry",
  surface_mesh: "Surface Mesh",
  volume_mesh: "Volume Mesh",
  run: "Run",
  saved_plot: "Saved Plot",
  sweep_group: "Sweep Group",
};

export function nodeTooltipContent(node: DAGNode): string {
  const raw = node as Record<string, unknown>;
  const kindLabel = KIND_LABELS[node.kind] ?? node.kind;
  const lines: string[] = [`${kindLabel}: ${node.name}`];

  lines.push(node.path);

  if (node.metadata?.status) lines.push(`Status: ${node.metadata.status}`);
  if (raw.is_stale) lines.push("⚠ Stale");

  const valueKeys = node.value_json ? Object.keys(node.value_json) : [];
  if (valueKeys.length > 0) {
    const preview = valueKeys.slice(0, 4).join(", ");
    const suffix = valueKeys.length > 4 ? ` (+${valueKeys.length - 4} more)` : "";
    lines.push(`Values: ${preview}${suffix}`);
  }

  return lines.join("\n");
}

export function plotTooltipContent(node: DAGNode): string {
  const vj = node.value_json as Record<string, unknown> | undefined;
  const chartType = vj?.chart_type ? String(vj.chart_type) : "chart";
  const lines: string[] = [`Plot: ${node.name}`, `Type: ${chartType}`];
  lines.push(node.path);
  return lines.join("\n");
}

export function sweepTooltipContent(group: SweepGroup): string {
  const lines: string[] = [
    `Sweep: ${group.title}`,
    `${group.runs.length} runs`,
  ];
  if (group.varyingKeys.length > 0) {
    lines.push(`Varying: ${group.varyingKeys.join(", ")}`);
  }
  return lines.join("\n");
}

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface EdgeTooltipInput {
  source: string;
  target: string;
  label?: string;
  operation?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

export interface EdgeTooltipNode {
  id: string;
  kind: string;
  name: string;
  created_by?: string;
  created_at?: string;
}

export function edgeTooltipContent(
  edge: EdgeTooltipInput,
  nodes: EdgeTooltipNode[],
): string {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);
  const sourceName = sourceNode ? `${KIND_LABELS[sourceNode.kind] ?? sourceNode.kind}: ${sourceNode.name}` : edge.source;
  const targetName = targetNode ? `${KIND_LABELS[targetNode.kind] ?? targetNode.kind}: ${targetNode.name}` : edge.target;
  const lines = [`${sourceName} → ${targetName}`];
  if (edge.label) lines.push(`Relationship: ${edge.label}`);
  if (edge.operation) lines.push(`Operation: ${edge.operation}`);

  // Prefer edge-level provenance; fall back to endpoint nodes
  const created = edge.created_at ?? targetNode?.created_at ?? sourceNode?.created_at;
  if (created) lines.push(`Created: ${formatRelativeTime(created)}`);

  const user = edge.metadata?.created_by ?? edge.metadata?.user
    ?? targetNode?.created_by ?? sourceNode?.created_by;
  if (user) lines.push(`By: ${String(user)}`);

  if (edge.metadata) {
    const entries = Object.entries(edge.metadata).filter(([k]) => k !== "created_by" && k !== "user");
    for (const [k, v] of entries.slice(0, 3)) {
      lines.push(`${k}: ${String(v)}`);
    }
    if (entries.length > 3) lines.push(`+${entries.length - 3} more`);
  }
  return lines.join("\n");
}
